import { useCallback, useEffect, useRef } from "react";
import { apiClient } from "@/lib/apiClient";
import { getToken } from "@/utils/auth";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";

/** Backend PRESENCE_HEARTBEAT_INTERVAL_SECONDS ile aynı. */
const HEARTBEAT_INTERVAL_MS = 30_000;

const noop = () => undefined;

/** Sayfa kapanırken çıkışı gönderir; axios istekleri sekme kapanınca iptal olur. */
function sendLeaveKeepalive(classroomId: string, presenceId: string) {
  const token = getToken();
  const base = (apiClient.defaults.baseURL ?? "").replace(/\/$/, "");
  try {
    void fetch(`${base}${virtualClassroomsApi.presenceLeavePath(classroomId, presenceId)}`, {
      method: "POST",
      keepalive: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }).catch(noop);
  } catch {
    // Gönderilemezse backend kaydı son sinyalden sonra kapatır.
  }
}

function createPresenceTracker(classroomId: string) {
  let active = false;
  let presenceId: string | null = null;
  let opening = false;
  let timer: number | undefined;

  const open = async () => {
    if (opening || presenceId) return;
    opening = true;
    try {
      const res = await virtualClassroomsApi.joinPresence(classroomId);
      if (!active) {
        // Kayıt açılırken odadan çıkıldı.
        void virtualClassroomsApi.leavePresence(classroomId, res.presenceId).catch(noop);
        return;
      }
      presenceId = res.presenceId;
    } catch {
      // Kayıt tutulamadı; ders sürer, sonraki sinyalde yeniden denenir.
    } finally {
      opening = false;
    }
  };

  const beat = async () => {
    if (!active) return;
    if (!presenceId) {
      await open();
      return;
    }
    try {
      await virtualClassroomsApi.heartbeatPresence(classroomId, presenceId);
    } catch (error) {
      // Kayıt kapanmışsa (ör. bilgisayar uykudan döndü) yenisini aç.
      if ((error as { response?: { status?: number } })?.response?.status === 404) {
        presenceId = null;
        await open();
      }
    }
  };

  return {
    join() {
      if (active) return;
      active = true;
      void open();
      timer = window.setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS);
    },
    leave(keepalive = false) {
      active = false;
      if (timer !== undefined) {
        window.clearInterval(timer);
        timer = undefined;
      }
      const id = presenceId;
      presenceId = null;
      if (!id) return;
      if (keepalive) {
        sendLeaveKeepalive(classroomId, id);
      } else {
        void virtualClassroomsApi.leavePresence(classroomId, id).catch(noop);
      }
    },
  };
}

/**
 * Canlı sınıf giriş-çıkış kaydı (yoklamada içeride kalınan süre).
 *
 * Jitsi odasına girildiğinde `markJoined`, çıkıldığında `markLeft` çağrılır.
 * İçerideyken 30 sn'de bir sinyal gider; sekme kapanırsa çıkış `keepalive` ile
 * gönderilir, o da ulaşmazsa backend kaydı son sinyalden sonra kapatır.
 */
export function useClassroomPresence(classroomId: string | null | undefined) {
  const trackerRef = useRef<ReturnType<typeof createPresenceTracker> | null>(null);

  useEffect(() => {
    if (!classroomId) return;
    const tracker = createPresenceTracker(classroomId);
    trackerRef.current = tracker;
    const handlePageHide = () => tracker.leave(true);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      tracker.leave();
      if (trackerRef.current === tracker) trackerRef.current = null;
    };
  }, [classroomId]);

  const markJoined = useCallback(() => trackerRef.current?.join(), []);
  const markLeft = useCallback(() => trackerRef.current?.leave(), []);

  return { markJoined, markLeft };
}
